import { useState, useMemo, useId, useRef, useEffect } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, ListFilter, X, GripVertical } from "lucide-react";
import { cn } from "@/utils/cn";
import type { DraftCalendarItem, DraftCalendarSortField, DraftCalendarSortDir, DraftCalendarTableProps } from "@/types/draftCalendar";
import { getSummitMeetingUrl } from "@/config/env";
import { InfoTooltip } from "@/features/dashboard/components/InfoTooltip";
import mablegridImg from "@/imgs/mablegrid.png";
import bellImg from "@/imgs/mablebellicon.png";
import { Statuses } from "@/utils/statuses";
import { DC_TABLE_COL, DC_TABLE_LABEL, getStatusDisplayLabel } from "@/utils/draftCalendarFields";
import { DateFilterDropdown } from "./DateFilterDropdown";
import "./DraftCalendarTable.css";



type SortField = DraftCalendarSortField;
type SortDir = DraftCalendarSortDir;

function matchesSearchValue(value: string | null | undefined, query: string): boolean {
  if (!value) return false;

  const normalizedValue = value.trim().toLocaleLowerCase();
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return true;
  if (normalizedValue.includes(normalizedQuery)) return true;

  const valueWords = normalizedValue.split(/\s+/);
  return normalizedQuery.split(/\s+/).every((queryWord) =>
    valueWords.some((valueWord) => valueWord.startsWith(queryWord))
  );
}

function getSortValue(item: DraftCalendarItem, field: SortField): string | number {
  switch (field) {
    case DC_TABLE_COL.MEETING_ID:
      if (item.meetingId) return `0_${item.meetingId}`;
      if (item.summitIntegrationStatus === "VALIDATION_ERROR" || item.summitIntegrationStatus === "SERVER_ERROR")
        return "1_error";
      if (item.summitIntegrationStatus === "PUSHED_TO_SUMMIT") return "2_pushed";
      return "3_none";
    case DC_TABLE_COL.SUMMIT_MEETING_STATUS:
      return item.summitMeetingStatusSortOrder;
    case DC_TABLE_COL.DU_MEETING_STATUS:
      return item.duMeetingStatusSortOrder;
    case DC_TABLE_COL.IS_READ:
      return item.isRead ? 1 : 0;
    case DC_TABLE_COL.UNSEEN_COMMENTS:
      return item.unSeenComments ? 1 : 0;
    case DC_TABLE_COL.SHOW_IN_GRC:
      return item.showInGRC ? 1 : 0;
    case DC_TABLE_COL.NIGHT_GUEST_NUMBER:
    case DC_TABLE_COL.ALL_IN_ATTENDEES:
      return item[field] ?? 0;
    case DC_TABLE_COL.LAST_UPDATED:
      return item.updatedDateFormat ?? "—";
    case DC_TABLE_COL.EVENT_START:
    case DC_TABLE_COL.EVENT_END:
    case DC_TABLE_COL.ROOM_BLOCK_START:
    case DC_TABLE_COL.ROOM_BLOCK_END:
      return item[field] ?? "";
    default:
      return (item[field] as string | null) ?? "";
  }
}

function sortItems(items: DraftCalendarItem[], field: SortField, dir: SortDir) {
  return [...items].sort((a, b) => {
    const va = getSortValue(a, field);
    const vb = getSortValue(b, field);
    const cmp = typeof va === "number" && typeof vb === "number"
      ? va - vb
      : String(va).localeCompare(String(vb));
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────
// DraftCalendarTableProps is defined in @/types/draftCalendar

function formatDate(d: string | null): string {
  if (!d) return "—";
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${days[date.getDay()]}, ${mm}-${dd}-${date.getFullYear()}`;
  } catch (error) {
    console.error("[DraftCalendarTable] formatDate failed", { input: d, error });
    return d;
  }
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField | null; sortDir: SortDir | null }) {
  if (sortField !== field || !sortDir) return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
  return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

// ─── Column definitions ───────────────────────────────────────────────────────

interface ColumnDef {
  key: SortField;
  label: string;
  tooltip?: string;
  commentTooltips?: {
    noComments: string;
    commentsRead: string;
    unreadComments: string;
  };
  className?: string;
  style?: React.CSSProperties;
  sortable?: boolean;
  /** When true a checkbox-list filter icon is shown in the column header */
  filterable?: boolean;
  /** Filter type: "text" (default flat checkbox list) or "date" (hierarchical Year > Month > Day) */
  filterType?: "text" | "date";
  visibility?: "always" | "admin-scheduler" | "toggle-settings";
  /** Optional custom header node — used for icon-only headers (e.g. mablegrid image) */
  renderHeader?: () => React.ReactNode;
  /** When true, force rendering the standard info icon next to custom/icon headers */
  showInfoIcon?: boolean;
}

const HEADER_ICON_CONFIG = {
  isRead: {
    alt: "Changes in meeting details",
    width: 20,
    height: 20,
  },
  showInGRC: {
    alt: "Show meeting in GRC",
    width: 20,
    height: 15,
  },
} as const;

function handleHeaderIconLoadError(iconName: string, event: React.SyntheticEvent<HTMLImageElement, Event>) {
  const source = event.currentTarget.currentSrc || event.currentTarget.src;
  console.error("[DraftCalendarTable] Failed to load header icon", { iconName, source });
}

const COLUMNS: ColumnDef[] = [
  // ── Fixed icon columns ───────────────────────────────────────────────────
  { key: DC_TABLE_COL.IS_READ, label: DC_TABLE_LABEL.IS_READ, tooltip: "Indicator for changes\n to meeting details", className: "w-8 dcf-icon-col--read", sortable: false, visibility: "always",
    showInfoIcon: true,
    renderHeader: () => (
      <div className="draft-toggle flex w-full items-center justify-center" >
        <img
          src={bellImg}
          alt={HEADER_ICON_CONFIG.isRead.alt}
          width={HEADER_ICON_CONFIG.isRead.width}
          height={HEADER_ICON_CONFIG.isRead.height}
          className="dcf-header-icon dcf-header-icon--read"
          onError={(event) => handleHeaderIconLoadError("isRead", event)}
        />
      </div>
    )
  },
  { key: DC_TABLE_COL.UNSEEN_COMMENTS, label: DC_TABLE_LABEL.UNSEEN_COMMENTS, tooltip: "Comments", className: "w-12", sortable: false, visibility: "always",
    showInfoIcon: true,
    commentTooltips: {
      noComments: "No Comments",
      commentsRead: "Comments exist - no unread",
      unreadComments: "Unread Comment(s)",
    },
    renderHeader: () => (
      <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.2413 2.5C18.2413 1.5375 17.4625 0.75 16.5 0.75H2.5C1.5375 0.75 0.75 1.5375 0.75 2.5V13C0.75 13.9625 1.5375 14.75 2.5 14.75H14.75L18.25 18.25L18.2413 2.5ZM16.5 2.5V14.0238L15.4763 13H2.5V2.5H16.5ZM4.25 9.5H14.75V11.25H4.25V9.5ZM4.25 6.875H14.75V8.625H4.25V6.875ZM4.25 4.25H14.75V6H4.25V4.25Z" fill="gray" />
      </svg>
    )
  },
  { key: DC_TABLE_COL.SHOW_IN_GRC, label: DC_TABLE_LABEL.SHOW_IN_GRC, tooltip: "Show meeting in GRC", className: "w-12 dcf-icon-col--grc", sortable: false, visibility: "admin-scheduler",
    showInfoIcon: true,
    renderHeader: () => (
      <div className="draft-toggle flex w-full items-center justify-center">
      <img
        src={mablegridImg}
        alt={HEADER_ICON_CONFIG.showInGRC.alt}
        width={HEADER_ICON_CONFIG.showInGRC.width}
        height={HEADER_ICON_CONFIG.showInGRC.height}
        className="dcf-header-icon dcf-header-icon--grc"
        onError={(event) => handleHeaderIconLoadError("showInGRC", event)}
      /></div>
    )
  },
  // ── Status columns ───────────────────────────────────────────────────────

  { key: DC_TABLE_COL.STATE,               label: DC_TABLE_LABEL.STATE, tooltip: "Status of the Meeting in the Calendar Process", className: "w-32",          filterable: true, visibility: "always" },
  // Summit / DU statuses — sortable via custom sort-order fields (mirrors Blazor)
  { key: DC_TABLE_COL.SUMMIT_MEETING_STATUS, label: DC_TABLE_LABEL.SUMMIT_MEETING_STATUS, className: "min-w-[180px]", sortable: true, filterable: true,  visibility: "admin-scheduler" },
  { key: DC_TABLE_COL.DU_MEETING_STATUS,     label: DC_TABLE_LABEL.DU_MEETING_STATUS,             className: "min-w-[120px]", sortable: true, filterable: true,  visibility: "admin-scheduler" },
  // ── Toggle-settings columns ──────────────────────────────────────────────
  { key: DC_TABLE_COL.STATUS,              label: DC_TABLE_LABEL.STATUS,                  tooltip: "Draft Calendar meeting tier", className: "w-16",          sortable: false, visibility: "toggle-settings" },
  { key: DC_TABLE_COL.PRIOR_ID,             label: DC_TABLE_LABEL.PRIOR_ID,              tooltip: "Prior Summit meeting ID", className: "w-20",          style: { width: "max-content", whiteSpace: "nowrap" }, visibility: "admin-scheduler" },
  // Mirrors Blazor: New ID column Visible="@GetPermission(\"CanViewToggleSettings\")"
  { key: DC_TABLE_COL.MEETING_ID,           label: DC_TABLE_LABEL.MEETING_ID,                tooltip: "New Summit meeting ID", className: "w-20",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: true, visibility: "admin-scheduler" },
  // ── Core meeting columns ─────────────────────────────────────────────────
  { key: DC_TABLE_COL.BUSINESS,            label: DC_TABLE_LABEL.BUSINESS,              className: "min-w-[120px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.MEETING_NAME,         label: DC_TABLE_LABEL.MEETING_NAME,          className: "min-w-[200px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.ROOM_BLOCK_START,      label: DC_TABLE_LABEL.ROOM_BLOCK_START,      tooltip: "First night guestrooms required, arrival day", className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, filterable: true, filterType: "date", visibility: "always" },
  { key: DC_TABLE_COL.ROOM_BLOCK_END,        label: DC_TABLE_LABEL.ROOM_BLOCK_END,        tooltip: "Last night guestrooms will be required, night before departure day", className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, filterable: true, filterType: "date", visibility: "always" },
  { key: DC_TABLE_COL.EVENT_START,          label: DC_TABLE_LABEL.EVENT_START,         tooltip: "First day of meeting curriculum", className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, filterable: true, filterType: "date", visibility: "always" },
  { key: DC_TABLE_COL.EVENT_END,            label: DC_TABLE_LABEL.EVENT_END,           tooltip: "Final day of meeting, departure day", className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, filterable: true, filterType: "date", visibility: "always" },
  { key: DC_TABLE_COL.START_TIME_STRING,     label: DC_TABLE_LABEL.START_TIME_STRING,            tooltip: "First day of meeting start time", className: "w-24",          visibility: "always" },
  { key: DC_TABLE_COL.END_TIME_STRING,       label: DC_TABLE_LABEL.END_TIME_STRING,              tooltip: "Final day of meeting end time", className: "w-24",          visibility: "always" },
  { key: DC_TABLE_COL.NIGHT_GUEST_NUMBER,    label: DC_TABLE_LABEL.NIGHT_GUEST_NUMBER,          tooltip: "Total overnight guests for all event nights combined", className: "w-20",          sortable: false, visibility: "always",
    showInfoIcon: true,
    renderHeader: () => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.375 9.9325V7.5C18.375 6.05625 17.1938 4.875 15.75 4.875H12.25C11.5763 4.875 10.9637 5.1375 10.5 5.5575C10.0363 5.1375 9.42375 4.875 8.75 4.875H5.25C3.80625 4.875 2.625 6.05625 2.625 7.5V9.9325C2.09125 10.4137 1.75 11.105 1.75 11.875V17.125H3.5V15.375H17.5V17.125H19.25V11.875C19.25 11.105 18.9088 10.4137 18.375 9.9325ZM12.25 6.625H15.75C16.2312 6.625 16.625 7.01875 16.625 7.5V9.25H11.375V7.5C11.375 7.01875 11.7688 6.625 12.25 6.625ZM4.375 7.5C4.375 7.01875 4.76875 6.625 5.25 6.625H8.75C9.23125 6.625 9.625 7.01875 9.625 7.5V9.25H4.375V7.5ZM3.5 13.625V11.875C3.5 11.3938 3.89375 11 4.375 11H16.625C17.1062 11 17.5 11.3938 17.5 11.875V13.625H3.5Z" fill="black" />
        </svg>
      </div>
    )
  },
  { key: DC_TABLE_COL.ALL_IN_ATTENDEES,           label: DC_TABLE_LABEL.ALL_IN_ATTENDEES,  tooltip: "Peak Number of All Attendees", className: "w-24",          visibility: "always" },
  { key: DC_TABLE_COL.MEETING_TYPE,              label: DC_TABLE_LABEL.MEETING_TYPE,          className: "min-w-[130px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.BUSINESS_LINE,             label: DC_TABLE_LABEL.BUSINESS_LINE,         className: "min-w-[140px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.ATTENDEE_LEVEL,            label: DC_TABLE_LABEL.ATTENDEE_LEVEL,        className: "min-w-[140px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.MEETING_CATEGORY,          label: DC_TABLE_LABEL.MEETING_CATEGORY,      className: "min-w-[140px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.PROGRAM_OWNER,   label: DC_TABLE_LABEL.PROGRAM_OWNER,className: "min-w-[200px]", visibility: "always" },
  { key: DC_TABLE_COL.PROGRAM_CONTACT,     label: DC_TABLE_LABEL.PROGRAM_CONTACT,       className: "min-w-[150px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.MEETING_PLANNER,     label: DC_TABLE_LABEL.MEETING_PLANNER,      className: "min-w-[150px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.PROGRAM_MANAGER,           label: DC_TABLE_LABEL.PROGRAM_MANAGER,       className: "min-w-[150px]", filterable: true, visibility: "always" },
  { key: DC_TABLE_COL.LAST_UPDATED,              label: DC_TABLE_LABEL.LAST_UPDATED,          className: "min-w-[170px]", visibility: "always" },
];

const UNSEEN_COMMENTS_TOOLTIPS =
  COLUMNS.find((col) => col.key === DC_TABLE_COL.UNSEEN_COMMENTS)?.commentTooltips ?? {
    noComments: "No Comments",
    commentsRead: "Comments exist - no unread",
    unreadComments: "Unread Comment(s)",
  };

// ─── Column filter helpers ────────────────────────────────────────────────────

/** Extract the text value used for checkbox-list filtering of a given column. */
function getFilterValue(item: DraftCalendarItem, key: SortField): string {
  const raw = (item as unknown as Record<string, unknown>)[key];
  if (raw === null || raw === undefined || raw === "") return "(Blank)";
  return String(raw);
}

// ─── FilterDropdown component ─────────────────────────────────────────────────

interface FilterDropdownProps {
  columnKey: SortField;
  label: string;
  allItems: DraftCalendarItem[];
  selected: string[];
  onApply: (values: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}

function FilterDropdown({ columnKey, label, allItems, selected, onApply, onClear, onClose }: FilterDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Unique sorted values derived from all items (not just the filtered rows)
  const options = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((item) => set.add(getFilterValue(item, columnKey)));
    return Array.from(set).sort((a, b) => {
      if (a === "(Blank)") return 1;
      if (b === "(Blank)") return -1;
      return a.localeCompare(b);
    });
  }, [allItems, columnKey]);

  const [localSelected, setLocalSelected] = useState<string[]>(() =>
    selected.length > 0 ? selected : options
  );

  const allChecked = localSelected.length === options.length;
  const noneChecked = localSelected.length === 0;

  // Close on outside click
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [onClose]);

  function toggleOption(val: string) {
    setLocalSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  }

  function toggleAll() {
    setLocalSelected(allChecked ? [] : options);
  }

  function handleApply() {
    // If all selected → effectively no filter
    if (localSelected.length === options.length) {
      onClear();
    } else {
      onApply(localSelected);
    }
    onClose();
  }

  function handleClear() {
    onClear();
    onClose();
  }

  return (
    <div ref={panelRef} className="dcf-filter-panel" role="dialog" aria-label={`Filter by ${label}`}>
      <div className="dcf-filter-header">
        <span className="dcf-filter-title">Filter: {label}</span>
        <button type="button" className="dcf-filter-close" onClick={onClose} aria-label="Close filter">
          <X size={13} />
        </button>
      </div>
      <div className="dcf-filter-select-all">
        <label className="dcf-filter-option">
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => { if (el) el.indeterminate = !allChecked && !noneChecked; }}
            onChange={toggleAll}
            className="dcf-filter-checkbox"
          />
          <span>(Select All)</span>
        </label>
      </div>
      <div className="dcf-filter-list">
        {options.map((opt) => (
          <label key={opt} className="dcf-filter-option">
            <input
              type="checkbox"
              checked={localSelected.includes(opt)}
              onChange={() => toggleOption(opt)}
              className="dcf-filter-checkbox"
            />
            <span title={opt}>{opt}</span>
          </label>
        ))}
      </div>
      <div className="dcf-filter-footer">
        <button type="button" className="dcf-filter-clear" onClick={handleClear}>Clear</button>
        <button type="button" className="dcf-filter-apply" onClick={handleApply}>OK</button>
      </div>
    </div>
  );
}

/**
 * Sortable, selectable Draft Calendar list table with full column parity to Blazor.
 */
export function DraftCalendarTable({
  items,
  searchTerm,
  selected,
  onSelectionChange,
  onRowClick,
  canEdit,
  canBulkAction,
  canToggleSettings = false,
  signOffProcessStarted = false,
  isAdmin = false,
  isScheduler = false,
  onToggleGRC,
  highlightedIds,
  highlightedFields,
  failedIds,
  onFilteredCountChange,
  onFilteredItemsChange,
}: DraftCalendarTableProps) {
  const [sortField, setSortField] = useState<SortField | null>("eventStart");
  const [sortDir, setSortDir] = useState<SortDir | null>("asc");
  const selectAllId = useId();
  // columnFilters: key → selected values (empty array = no filter applied)
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);

  // ── Column resize ────────────────────────────────────────────────────────
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const thRefs = useRef<Record<string, HTMLTableCellElement | null>>({})

  // ── Column reorder ──────────────────────────────────────────────────
  // Stores ALL column keys in user-defined order (including invisible ones,
  // so the relative order is preserved when visibility toggles).
  // Persisted to sessionStorage so the arrangement survives page refreshes
  // within the same browser tab but resets when the tab is closed.
  const COLUMN_ORDER_KEY = "dcf_column_order";
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const stored = sessionStorage.getItem(COLUMN_ORDER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        // Merge: keep stored order for known keys, append any new columns at the end
        const allKeys = COLUMNS.map((c) => c.key);
        const known = new Set<string>(allKeys);
        const ordered = parsed.filter((k) => known.has(k));
        const missing = allKeys.filter((k) => !ordered.includes(k));
        return [...ordered, ...missing];
      }
    } catch { /* ignore parse errors */ }
    return COLUMNS.map((c) => c.key);
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
    } catch { /* ignore storage errors (e.g. private mode quota) */ }
  }, [columnOrder]);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  function handleColDragStart(key: string, e: React.DragEvent<HTMLDivElement>) {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  }

  function handleColDragOver(key: string, e: React.DragEvent<HTMLTableCellElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (key !== dragKey) setDragOverKey(key);
  }

  function handleColDrop(targetKey: string, e: React.DragEvent<HTMLTableCellElement>) {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey) return;
    setColumnOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(dragKey);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragKey);
      return next;
    });
    setDragKey(null);
    setDragOverKey(null);
  }

  function handleColDragEnd() {
    setDragKey(null);
    setDragOverKey(null);
  }
  const resizingRef = useRef<{
    key: string;
    startX: number;
    startWidth: number;
    /** Width that was set before this drag began — restored on Escape/cancel. */
    preResizeWidth: number | undefined;
  } | null>(null);

  function startResize(key: string, e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const th = thRefs.current[key];
    if (!th) return;

    const startWidth = th.getBoundingClientRect().width;
    resizingRef.current = {
      key,
      startX: e.clientX,
      startWidth,
      preResizeWidth: colWidths[key], // saved so Escape can restore exactly
    };

    // Capturing the pointer on the handle ensures pointermove/pointerup continue
    // firing even when the cursor leaves the element (mouse, stylus, touch).
    e.currentTarget.setPointerCapture(e.pointerId);

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onPointerMove(ev: PointerEvent) {
      if (!resizingRef.current) return;
      const delta = ev.clientX - resizingRef.current.startX;
      const newWidth = Math.max(40, resizingRef.current.startWidth + delta);
      setColWidths((prev) => ({ ...prev, [resizingRef.current!.key]: newWidth }));
    }

    function cleanupListeners() {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerCancel);
      document.removeEventListener("keydown", onKeyDown);
    }

    function onPointerUp() {
      resizingRef.current = null;
      cleanupListeners();
    }

    /** Pointer capture lost (e.g. browser interrupt, touch stolen) — cancel like Escape. */
    function onPointerCancel() {
      if (resizingRef.current) {
        const { key: k, preResizeWidth } = resizingRef.current;
        setColWidths((prev) => {
          const next = { ...prev };
          if (preResizeWidth !== undefined) {
            next[k] = preResizeWidth;
          } else {
            delete next[k];
          }
          return next;
        });
      }
      resizingRef.current = null;
      cleanupListeners();
    }

    /** Escape restores the column to its pre-drag width and cancels the resize. */
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        ev.preventDefault();
        onPointerCancel();
      }
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
    document.addEventListener("keydown", onKeyDown);
  }

  // Filter columns based on visibility rules
  const visibleColumns = useMemo(() => {
    return COLUMNS.filter((col) => {
      switch (col.visibility) {
        case "toggle-settings":
          return canToggleSettings;
        case "admin-scheduler":
          return isAdmin || isScheduler;
        case "always":
        default:
          return true;
      }
    });
  }, [canToggleSettings, isAdmin, isScheduler]);

  // Visible columns sorted by the user-defined column order
  const orderedVisibleColumns = useMemo(() => {
    const visibleMap = new Map(visibleColumns.map((c) => [c.key, c]));
    return columnOrder
      .filter((key) => visibleMap.has(key as SortField))
      .map((key) => visibleMap.get(key as SortField)!);
  }, [visibleColumns, columnOrder]);

  // Client-side filter by meeting name / business / ID
  const filtered = useMemo(() => {
    const q = searchTerm.trim();
    return q
      ? items.filter(
          (i) =>
            matchesSearchValue(i.meetingName, q) ||
            matchesSearchValue(i.business, q) ||
            matchesSearchValue(i.meetingId, q)
        )
      : items;
  }, [items, searchTerm]);

  // Apply checkbox-list column filters on top of the search-term filter
  const dateFilterColumns = useMemo(
    () => new Set(COLUMNS.filter((c) => c.filterType === "date").map((c) => c.key)),
    []
  );

  const afterColumnFilters = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([, vals]) => vals.length > 0
    );
    if (activeFilters.length === 0) return filtered;
    return filtered.filter((item) =>
      activeFilters.every(([key, vals]) => {
        if (dateFilterColumns.has(key as SortField)) {
          const raw = (item as unknown as Record<string, unknown>)[key];
          if (!raw) return vals.includes("(Blank)");
          const d = new Date(raw as string);
          if (Number.isNaN(d.getTime())) return false;
          const itemKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          return vals.includes(itemKey);
        }
        return vals.includes(getFilterValue(item, key as SortField));
      })
    );
  }, [filtered, columnFilters, dateFilterColumns]);

  const sorted = useMemo(
    () => sortField && sortDir ? sortItems(afterColumnFilters, sortField, sortDir) : afterColumnFilters,
    [afterColumnFilters, sortField, sortDir]
  );

  // Report filtered count and items (after column filters) back to parent for status bar / export
  useEffect(() => {
    onFilteredCountChange?.(afterColumnFilters.length);
    onFilteredItemsChange?.(afterColumnFilters);
  }, [afterColumnFilters, onFilteredCountChange, onFilteredItemsChange]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      if (sortDir === "desc") {
        setSortDir("asc");
      } else {
        // asc → clear (default unsorted)
        setSortField(null);
        setSortDir(null);
      }
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }

 function toggleAll() {
    if (selected.size === sorted.length && sorted.length > 0) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(sorted.map((i) => i.id)));
    }
  }

  function toggleOne(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  const selectableCount = sorted.length;
  const allSelected = selectableCount > 0 && selected.size === selectableCount;
  const someSelected = selected.size > 0 && selected.size < selectableCount;

  const activeFilterCount = Object.values(columnFilters).filter((v) => v.length > 0).length;

  function clearAllFilters() {
    setColumnFilters({});
  }

  return (
    <div className="draft-calendar-table-scroll">
      {/* Active filter indicator row */}
      {activeFilterCount > 0 && (
        <div className="dcf-active-filters-bar">
          <span>{activeFilterCount} column filter{activeFilterCount > 1 ? "s" : ""} active</span>
          <button type="button" className="dcf-clear-all-btn" onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      )}
      <table className="w-full border-collapse text-sm" aria-label="Draft calendar meetings">
        <thead className="sticky top-0 z-10 bg-[#E8E8E8]">
          <tr className="border-b border-slate-200">
            {/* Checkbox column */}
            {canBulkAction && (
              <th scope="col" className="w-10 px-3 py-2">
                <input
                  id={selectAllId}
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </th>
            )}



            {orderedVisibleColumns.map((col) => (
              (() => {
                const tooltipText = col.tooltip;
                const headerContent = col.renderHeader ? col.renderHeader() : col.label;
                const isTextHeader = !col.renderHeader;
                const shouldShowInfoIcon = !!tooltipText && (isTextHeader || col.showInfoIcon);
                const headerTooltip = shouldShowInfoIcon && tooltipText
                  ? <InfoTooltip text={tooltipText} placement="top" contentClassName={cn("dcf-header-tooltip dcf-tooltip-auto-width", col.key === "signOffStatus" && "dcf-header-tooltip-left-nowrap")} renderInPortal />
                  : null;

                return (

              <th
                key={col.key}
                ref={(el) => { thRefs.current[col.key] = el; }}
                scope="col"
                className={cn(
                  "px-3 py-2 text-left text-xs font-semibold text-black dcf-resizable-th",
                  col.className,
                  dragKey === col.key && "dcf-col-dragging",
                  dragOverKey === col.key && "dcf-col-drag-over"
                )}
                style={{
                  ...col.style,
                  ...(colWidths[col.key] ? { width: colWidths[col.key], minWidth: colWidths[col.key] } : {}),
                }}
                aria-sort={
                  sortField === col.key && sortDir
                    ? sortDir === "asc" ? "ascending" : "descending"
                    : undefined
                }
                onDragOver={(e) => handleColDragOver(col.key, e)}
                onDrop={(e) => handleColDrop(col.key, e)}
              >
                <div className="dcf-col-header">
                  {/* Drag-to-reorder grip handle */}
                  <div
                    className="dcf-col-drag-handle"
                    draggable
                    onDragStart={(e) => handleColDragStart(col.key, e)}
                    onDragEnd={handleColDragEnd}
                    title="Drag to reorder column"
                    aria-label={`Drag to reorder ${col.label} column`}
                  >
                    <GripVertical size={12} />
                  </div>
                  {col.sortable !== false ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                    >
                      {isTextHeader ? (
                        <span>{col.label}</span>
                      ) : (
                        headerContent
                      )}
                      {headerTooltip}
                      <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {isTextHeader ? (
                        <span>{col.label}</span>
                      ) : (
                        headerContent
                      )}
                      {headerTooltip}
                    </span>
                  )}
                  {col.filterable && (
                    <div className="dcf-filter-btn-wrap">
                      <button
                        type="button"
                        className={cn(
                          "dcf-filter-btn",
                          columnFilters[col.key]?.length > 0 && "dcf-filter-btn--active"
                        )}
                        title={`Filter ${col.label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenFilterCol((prev) => (prev === col.key ? null : col.key));
                        }}
                        aria-pressed={openFilterCol === col.key}
                        aria-label={`Filter ${col.label}`}
                      >
                        <ListFilter size={12} />
                      </button>
                      {openFilterCol === col.key && (
                        col.filterType === "date" ? (
                          <DateFilterDropdown
                            columnKey={col.key}
                            label={col.label}
                            allItems={items}
                            selected={columnFilters[col.key] ?? []}
                            onApply={(vals) =>
                              setColumnFilters((prev) => ({ ...prev, [col.key]: vals }))
                            }
                            onClear={() =>
                              setColumnFilters((prev) => {
                                const next = { ...prev };
                                delete next[col.key];
                                return next;
                              })
                            }
                            onClose={() => setOpenFilterCol(null)}
                          />
                        ) : (
                          <FilterDropdown
                            columnKey={col.key}
                            label={col.label}
                            allItems={items}
                            selected={columnFilters[col.key] ?? []}
                            onApply={(vals) =>
                              setColumnFilters((prev) => ({ ...prev, [col.key]: vals }))
                            }
                            onClear={() =>
                              setColumnFilters((prev) => {
                                const next = { ...prev };
                                delete next[col.key];
                                return next;
                              })
                            }
                            onClose={() => setOpenFilterCol(null)}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
                {/* Resize handle — drag to resize this column */}
                <div
                  className="dcf-col-resize-handle"
                  onPointerDown={(e) => startResize(col.key, e)}
                  aria-hidden="true"
                />
              </th>
                );
              })()
            ))}

          </tr>
        </thead>

        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td
                colSpan={canBulkAction ? visibleColumns.length + 1 : visibleColumns.length}
                className="py-10 text-center text-sm text-slate-500"
              >
                No meetings match your search.
              </td>
            </tr>
          ) : (
            sorted.map((item, idx) => (
              <tr
                key={item.id}
                className={cn(
                  "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                  failedIds?.has(item.id)
                    ? "failed-meeting-row"
                    : item.state?.toLowerCase() === "removed"
                    ? "removed-meeting-row"
                    : item.state?.toLowerCase() === "du declined"
                    ? "du-declined-meeting-row"
                    : idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]",
                  selected.has(item.id) && "ring-1 ring-inset ring-slate-400"
                )}
                onDoubleClick={() => onRowClick(item)}
              >
                {canBulkAction && (
                  <td
                    className={cn(
                      "px-3 py-2",
                      highlightedIds?.has(item.id) && !failedIds?.has(item.id) && "successful-meeting-selection-cell"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.meetingName ?? item.id}`}
                      checked={selected.has(item.id) || highlightedIds?.has(item.id) || failedIds?.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      className={cn(
                        "h-4 w-4 rounded border-slate-300"
                      )}
                    />
                  </td>
                )}

                {/* Render cells for visible columns */}
                {orderedVisibleColumns.map((col) => (
                  <td
                    key={`${item.id}-${col.key}`}
                    className={cn(
                      "px-3 py-2 text-slate-700",
                      col.className,
                      highlightedIds?.has(item.id) && highlightedFields?.has(col.key) && !failedIds?.has(item.id) && "dcf-cell--highlighted"
                    )}
                    style={failedIds?.has(item.id) ? { ...col.style, backgroundColor: 'inherit' } : col.style}
                    onClick={(e) => col.key === DC_TABLE_COL.SHOW_IN_GRC && e.stopPropagation()}
                  >
                    <CellRenderer
                      item={item}
                      field={col.key}
                      canToggleSettings={canToggleSettings}
                      isAdminOrScheduler={isAdmin || isScheduler}
                      onToggleGRC={onToggleGRC}
                    />
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Cell Renderer ──────────────────────────────────────────────────────────────

interface CellRendererProps {
  item: DraftCalendarItem;
  field: SortField;
  canToggleSettings: boolean;
  isAdminOrScheduler: boolean;
  onToggleGRC?: (id: number, showInGRC: boolean) => void;
}

function CellRenderer({ item, field, canToggleSettings, isAdminOrScheduler, onToggleGRC }: CellRendererProps) {
  switch (field) {
    case DC_TABLE_COL.IS_READ:
      return item.isRead ? (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" y="0.5" width="10" height="10" rx="5" fill="#BBBCBC" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" y="0.5" width="10" height="10" rx="5" fill="#007CB0" />
        </svg>
      );

    case DC_TABLE_COL.UNSEEN_COMMENTS:
      if (item.hasComments && item.unSeenComments) {
        return (
          <InfoTooltip text={UNSEEN_COMMENTS_TOOLTIPS.unreadComments} placement="bottom" contentClassName="dcf-tooltip-auto-width dcf-comment-tooltip-nowrap">
            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.2413 3.5C19.2413 2.5375 18.4625 1.75 17.5 1.75H3.5C2.5375 1.75 1.75 2.5375 1.75 3.5V14C1.75 14.9625 2.5375 15.75 3.5 15.75H15.75L19.25 19.25L19.2413 3.5ZM15.75 12.25H5.25V10.5H15.75V12.25ZM15.75 9.625H5.25V7.875H15.75V9.625ZM15.75 7H5.25V5.25H15.75V7Z" fill="black" />
              <circle cx="17" cy="5" r="4" fill="#C20000" />
            </svg>
          </InfoTooltip>
        );
      }
      if (item.hasComments) {
        return (
          <InfoTooltip text={UNSEEN_COMMENTS_TOOLTIPS.commentsRead} placement="bottom" contentClassName="dcf-tooltip-auto-width dcf-comment-tooltip-nowrap">
            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.2413 3.5C19.2413 2.5375 18.4625 1.75 17.5 1.75H3.5C2.5375 1.75 1.75 2.5375 1.75 3.5V14C1.75 14.9625 2.5375 15.75 3.5 15.75H15.75L19.25 19.25L19.2413 3.5ZM15.75 12.25H5.25V10.5H15.75V12.25ZM15.75 9.625H5.25V7.875H15.75V9.625ZM15.75 7H5.25V5.25H15.75V7Z" fill="black" />
            </svg>
          </InfoTooltip>
        );
      }
      return (
        <InfoTooltip text={UNSEEN_COMMENTS_TOOLTIPS.noComments} placement="bottom" contentClassName="dcf-tooltip-auto-width dcf-comment-tooltip-nowrap">
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.2413 2.5C18.2413 1.5375 17.4625 0.75 16.5 0.75H2.5C1.5375 0.75 0.75 1.5375 0.75 2.5V13C0.75 13.9625 1.5375 14.75 2.5 14.75H14.75L18.25 18.25L18.2413 2.5ZM16.5 2.5V14.0238L15.4763 13H2.5V2.5H16.5ZM4.25 9.5H14.75V11.25H4.25V9.5ZM4.25 6.875H14.75V8.625H4.25V6.875ZM4.25 4.25H14.75V6H4.25V4.25Z" fill="gray" />
          </svg>
        </InfoTooltip>
      );

    case DC_TABLE_COL.SHOW_IN_GRC: {
      if (!canToggleSettings) return null;
      // Mirrors Blazor: disabled when State == Removed || State == DUDeclined
      const isStateDisabled = item.state === "Removed" || item.state === "DU Declined";
      const toggleTitle = isStateDisabled
        ? "Cannot update GRC: meeting is in Removed or DU Declined state"
        : item.showInGRC ? "In GRC \u2013 click to remove" : "Not in GRC \u2013 click to add";
      return (
        <div className="center-checkbox" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            role="switch"
            aria-checked={item.showInGRC}
            aria-label={item.showInGRC ? "In GRC – click to remove" : "Not in GRC – click to add"}
            title={toggleTitle}
            disabled={isStateDisabled}
            onClick={() => {
              if (!isStateDisabled) onToggleGRC?.(item.id, !item.showInGRC);
            }}
            className="relative h-5 w-9 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
            style={item.showInGRC ? { background: "#26890D" } : { background: "#111" }}
          >
            <span
              className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
              style={{ left: item.showInGRC ? "18px" : "2px" }}
            />
          </button>
        </div>
      );
    }

    case DC_TABLE_COL.STATE:
      return <StateBadge state={getStatusDisplayLabel(item.state ?? "", isAdminOrScheduler)} />;

    case DC_TABLE_COL.STATUS: {
      const tier = (item.status ?? "").toLowerCase();
      if (!tier) return null;
      const cssClass = tier === "core" ? "event-status-draft-Core" : tier === "secondary" ? "event-status-draft-Secondary" : "";
      return (
        <div style={{ display: "grid", justifyContent: "center" }}>
          <span className={cssClass} title={item.status ?? ""} style={{ flexShrink: 0 }} />
        </div>
      );
    }

    case DC_TABLE_COL.PRIOR_ID:
      return <span className="text-xs">{item.priorId ?? "—"}</span>;

    case DC_TABLE_COL.MEETING_ID: {
      const meetingId = item.meetingId;
      const hasMeetingId = !!meetingId;
      const showPushed = !hasMeetingId && item.state === Statuses.Resolved;
      const showError = !hasMeetingId && (item.summitIntegrationStatus === "VALIDATION_ERROR" || item.state === "SERVER_ERROR");
      const summitUrl = hasMeetingId ? getSummitMeetingUrl(meetingId) : "";
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%" }}>
          {hasMeetingId && summitUrl ? (
            <a href={summitUrl} target="_blank" rel="noopener noreferrer"
              className="meeting-id-link-cell" onClick={(e) => e.stopPropagation()}>
              {meetingId}
            </a>
          ) : hasMeetingId ? (
            <span className="text-xs font-mono" style={{ color: "#007CB0" }}>{meetingId}</span>
          ) : null}
          {!hasMeetingId && showError && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.9925 1.5C4.8525 1.5 1.5 4.86 1.5 9C1.5 13.14 4.8525 16.5 8.9925 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 8.9925 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15ZM9.375 5.25H8.25V9.75L12.1875 12.1125L12.75 11.19L9.375 9.1875V5.25Z" fill="#CC1616" />
            </svg>
          )}
          {!hasMeetingId && !showError && showPushed && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.9925 1.5C4.8525 1.5 1.5 4.86 1.5 9C1.5 13.14 4.8525 16.5 8.9925 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 8.9925 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15ZM9.375 5.25H8.25V9.75L12.1875 12.1125L12.75 11.19L9.375 9.1875V5.25Z" fill="#838383" />
            </svg>
          )}
        </div>
      );
    }

    case DC_TABLE_COL.BUSINESS:
      return <span title={item.business ?? ""}>{item.business ?? "—"}</span>;

    case DC_TABLE_COL.MEETING_NAME:
      return (
        <span className="block truncate font-medium text-slate-900" title={item.meetingName ?? ""}>
          {item.meetingName ?? "—"}
        </span>
      );

    case DC_TABLE_COL.ROOM_BLOCK_START:
      return <span className="text-xs">{formatDate(item.roomBlockStart)}</span>;
    case DC_TABLE_COL.ROOM_BLOCK_END:
      return <span className="text-xs">{formatDate(item.roomBlockEnd)}</span>;
    case DC_TABLE_COL.EVENT_START:
      return <span className="text-xs">{formatDate(item.eventStart)}</span>;
    case DC_TABLE_COL.EVENT_END:
      return <span className="text-xs">{formatDate(item.eventEnd)}</span>;
    case DC_TABLE_COL.START_TIME_STRING:
      return <span className="text-xs">{item.startTimeString || "—"}</span>;
    case DC_TABLE_COL.END_TIME_STRING:
      return <span className="text-xs">{item.endTimeString || "—"}</span>;

    case DC_TABLE_COL.NIGHT_GUEST_NUMBER: {
      // Mirrors Blazor tooltip-target-nightguest: split NightGuestTooltip by ';' and show each line
      const tooltipLines = item.nightGuestTooltip
        ? item.nightGuestTooltip.split(";").map((s) => s.trim()).filter(Boolean)
        : [];
      const tooltipText =
        tooltipLines.length > 0
          ? tooltipLines.join("\n")
          : `Night guests: ${item.nightGuestNumber ?? 0}`;
      return (
        <span className="inline-flex items-center gap-1 text-xs">
          {item.nightGuestNumber ?? "\u2014"}
          <InfoTooltip text={tooltipText} contentClassName="nightguest-tooltip-content dcf-tooltip-auto-width" />
        </span>
      );
    }

    case DC_TABLE_COL.ALL_IN_ATTENDEES:
      return <span className="text-xs">{item.allInAttendees ?? "—"}</span>;
    case DC_TABLE_COL.MEETING_TYPE:
      return <span className="text-xs" title={item.meetingType ?? ""}>{item.meetingType ?? "—"}</span>;
    case DC_TABLE_COL.BUSINESS_LINE:
      return <span className="text-xs" title={item.businessLine ?? ""}>{item.businessLine ?? "—"}</span>;
    case DC_TABLE_COL.ATTENDEE_LEVEL:
      return <span className="text-xs" title={item.attendeeLevel ?? ""}>{item.attendeeLevel ?? "—"}</span>;
    case DC_TABLE_COL.MEETING_CATEGORY:
      return <span className="text-xs" title={item.meetingCategory ?? ""}>{item.meetingCategory ?? "—"}</span>;
    case DC_TABLE_COL.PROGRAM_OWNER:
      return <span className="text-xs" title={item.summitProgramOwnerName ?? ""}>{item.summitProgramOwnerName ?? "—"}</span>;
    case DC_TABLE_COL.PROGRAM_CONTACT:
      return <span className="text-xs" title={item.summitProgramContact ?? ""}>{item.summitProgramContact ?? "—"}</span>;
    case DC_TABLE_COL.MEETING_PLANNER:
      return <span className="text-xs" title={item.summitMeetingPlanner ?? ""}>{item.summitMeetingPlanner ?? "—"}</span>;
    case DC_TABLE_COL.PROGRAM_MANAGER:
      return <span className="text-xs" title={item.programManager ?? ""}>{item.programManager ?? "—"}</span>;
    case DC_TABLE_COL.LAST_UPDATED: {
      return <span className="text-xs" title={item.updatedDateFormat ?? ""}>{item.updatedDateFormat ?? "—"}</span>;
    }
    case DC_TABLE_COL.SUMMIT_MEETING_STATUS:
      return <span className="text-xs">{item.summitMeetingStatus ?? "—"}</span>;
    case DC_TABLE_COL.DU_MEETING_STATUS:
      return <span className="text-xs">{item.duMeetingStatus ?? "—"}</span>;

    default:
      return null;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const STATE_COLORS: Record<string, string> = {
  [Statuses.Draft]:             "bg-slate-100 text-slate-700",
  [Statuses.NewRequest]:        "bg-slate-100 text-slate-700",
  [Statuses.ProgramOwnerReviewed]: "bg-blue-50 text-blue-700",
  [Statuses.Resolved]:          "bg-green-100 text-green-800",
  [Statuses.CreatedInSummit]:   "bg-green-100 text-green-800",
  [Statuses.ConfirmedEvent]:    "bg-green-100 text-green-800",
  [Statuses.DUDeclined]:        "bg-orange-100 text-orange-700",
  [Statuses.Removed]:           "bg-red-100 text-red-700",
  [Statuses.Softhold]:          "bg-yellow-100 text-yellow-800",
  [Statuses.DidNotMaterialize]: "bg-slate-100 text-slate-500",
  [Statuses.CNFCREATEDSCS]:     "bg-slate-100 text-slate-600",
  // Legacy / extra values seen in existing data
  Submitted:  "bg-blue-100 text-blue-700",
  Confirmed:  "bg-green-100 text-green-800",
  Cancelled:  "bg-red-100 text-red-700",
  Declined:   "bg-orange-100 text-orange-700",
};

function StateBadge({ state }: { state: string }) {
  return (
    <span className="text-xs font-medium whitespace-nowrap text-slate-800">
      {state}
    </span>
  );
}

